import { useId } from 'react';
import type { ChartKind, ReportRow, ValueFormat } from '@goyone/shared';
import { money, num } from '../lib/format';

/**
 * رسوم التقارير.
 *
 * مكتوبة بـSVG مباشرة لا بمكتبة رسوم. السبب عملي لا مبدئي: المكتبات
 * الجاهزة تفترض اتجاهًا من اليسار لليمين في المحاور والتلميحات وترتيب
 * المفاتيح، ومعالجة ذلك في كل رسم أطول من كتابة ثلاثة أشكال بسيطة.
 */

/** ألوان القطاعات: متمايزة بما يكفي للتفريق دون أن تتنافر. */
const PALETTE = [
  '#3672ea',
  '#8ce553',
  '#edda2e',
  '#5596ff',
  '#f2994a',
  '#9b51e0',
  '#2d9cdb',
  '#eb5757',
  '#27ae60',
  '#bb6bd9',
];

export function formatValue(value: number, format: ValueFormat): string {
  if (format === 'MONEY') return money(value);
  if (format === 'PERCENT') return `${num(value)}٪`;
  if (format === 'DAYS') return `${num(value)} يومًا`;
  return num(value);
}

export function Chart({
  kind,
  rows,
  format,
}: {
  kind: ChartKind;
  rows: readonly ReportRow[];
  format: ValueFormat;
}) {
  if (rows.length === 0) return null;
  if (kind === 'PIE') return <PieChart rows={rows} format={format} />;
  if (kind === 'LINE') return <LineChart rows={rows} format={format} />;
  return <BarChart rows={rows} format={format} />;
}

/* ==========================================================================
   أعمدة أفقية
   ========================================================================== */

function BarChart({ rows, format }: { rows: readonly ReportRow[]; format: ValueFormat }) {
  /*
   * الأعمدة أفقية لا رأسية.
   *
   * أسماء الفئات بالعربية طويلة — "إدارة حسابات التواصل الاجتماعي" — وفي
   * الرسم الرأسي تُقلَب أو تُقصّ. الأفقي يمنحها سطرًا كاملًا.
   */
  const max = Math.max(...rows.map((r) => Math.abs(r.value)), 1);

  return (
    <div className="chart-bars">
      {rows.map((row, i) => (
        <div key={row.key} className="chart-bar-row">
          <div className="chart-bar-label" title={row.label}>
            {row.label}
          </div>
          <div className="chart-bar-track">
            <div
              className="chart-bar-fill"
              style={{
                width: `${(Math.abs(row.value) / max) * 100}%`,
                background: PALETTE[i % PALETTE.length],
              }}
            />
          </div>
          <div className="chart-bar-value num">{formatValue(row.value, format)}</div>
          <div className="chart-bar-share tiny mute num">{row.share}٪</div>
        </div>
      ))}
    </div>
  );
}

/* ==========================================================================
   خط زمني
   ========================================================================== */

function LineChart({ rows, format }: { rows: readonly ReportRow[]; format: ValueFormat }) {
  const width = 720;
  const height = 260;
  const padX = 12;
  const padTop = 16;
  const padBottom = 34;

  const values = rows.map((r) => r.value);
  const max = Math.max(...values, 0);
  const min = Math.min(...values, 0);
  const span = max - min || 1;

  const innerW = width - padX * 2;
  const innerH = height - padTop - padBottom;

  /*
   * المحور الزمني يسير من اليمين إلى اليسار.
   *
   * القارئ العربي يبدأ من اليمين، فوضع الأقدم يمينًا يجعل الزمن يتقدّم في
   * اتجاه القراءة نفسه. عكسه يجعل الرسم يُقرأ بالمقلوب دون أن ينتبه أحد.
   */
  const x = (i: number) =>
    rows.length === 1 ? width / 2 : width - padX - (i / (rows.length - 1)) * innerW;
  const y = (v: number) => padTop + innerH - ((v - min) / span) * innerH;

  const line = rows.map((r, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(r.value)}`).join(' ');
  const area = `${line} L ${x(rows.length - 1)} ${padTop + innerH} L ${x(0)} ${padTop + innerH} Z`;
  const gradientId = useId();

  // ثلاثة خطوط إرشادية تكفي لتقدير القيمة دون أن تزحم الرسم.
  const guides = [0, 0.5, 1].map((f) => min + span * f);

  return (
    <div className="chart-line-wrap">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="chart-svg"
        role="img"
        aria-label="رسم بياني خطي"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3672ea" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#3672ea" stopOpacity="0" />
          </linearGradient>
        </defs>

        {guides.map((g, i) => (
          <line
            key={i}
            x1={padX}
            x2={width - padX}
            y1={y(g)}
            y2={y(g)}
            className="chart-guide"
          />
        ))}

        <path d={area} fill={`url(#${gradientId})`} />
        <path d={line} className="chart-line" />

        {rows.map((r, i) => (
          <g key={r.key}>
            <circle cx={x(i)} cy={y(r.value)} r="3.5" className="chart-dot" />
            <title>{`${r.label}: ${formatValue(r.value, format)}`}</title>
          </g>
        ))}

        {rows.map((r, i) => {
          // تسمية كل نقطة تتراكب عند كثرة الفترات، فتُعرض بالتناوب.
          const step = Math.ceil(rows.length / 8);
          if (i % step !== 0 && i !== rows.length - 1) return null;
          return (
            <text key={r.key} x={x(i)} y={height - 12} className="chart-axis-label">
              {r.label}
            </text>
          );
        })}
      </svg>

      <div className="chart-line-legend">
        {rows.map((r) => (
          <div key={r.key} className="chart-legend-item">
            <span className="tiny mute">{r.label}</span>
            <span className="tiny num strong">{formatValue(r.value, format)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ==========================================================================
   قطاعات
   ========================================================================== */

function PieChart({ rows, format }: { rows: readonly ReportRow[]; format: ValueFormat }) {
  const size = 240;
  const radius = 100;
  const inner = 58;
  const centre = size / 2;

  const total = rows.reduce((s, r) => s + Math.abs(r.value), 0) || 1;

  let angle = -Math.PI / 2;
  const slices = rows.map((row, i) => {
    const portion = Math.abs(row.value) / total;
    const start = angle;
    const end = angle + portion * Math.PI * 2;
    angle = end;

    const large = end - start > Math.PI ? 1 : 0;
    const p = (r: number, a: number) => `${centre + r * Math.cos(a)} ${centre + r * Math.sin(a)}`;

    /*
     * القطاع الوحيد لا يُرسم بقوس: نقطتا بدايته ونهايته تتطابقان فيختفي.
     * الدائرة الكاملة تُرسم كحلقة بدل ذلك.
     */
    const d =
      portion >= 0.9999
        ? `M ${centre} ${centre - radius} A ${radius} ${radius} 0 1 1 ${centre - 0.01} ${centre - radius} L ${centre - 0.01} ${centre - inner} A ${inner} ${inner} 0 1 0 ${centre} ${centre - inner} Z`
        : `M ${p(radius, start)} A ${radius} ${radius} 0 ${large} 1 ${p(radius, end)} L ${p(inner, end)} A ${inner} ${inner} 0 ${large} 0 ${p(inner, start)} Z`;

    return { row, d, colour: PALETTE[i % PALETTE.length]! };
  });

  return (
    <div className="chart-pie-wrap">
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="chart-pie"
        role="img"
        aria-label="رسم بياني دائري"
      >
        {slices.map(({ row, d, colour }) => (
          <path key={row.key} d={d} fill={colour} className="chart-slice">
            <title>{`${row.label}: ${formatValue(row.value, format)} (${row.share}٪)`}</title>
          </path>
        ))}
      </svg>

      <div className="chart-legend">
        {slices.map(({ row, colour }) => (
          <div key={row.key} className="chart-legend-row">
            <span className="chart-swatch" style={{ background: colour }} />
            <span className="small" style={{ flex: 1 }}>
              {row.label}
            </span>
            <span className="num small">{formatValue(row.value, format)}</span>
            <span className="tiny mute num">{row.share}٪</span>
          </div>
        ))}
      </div>
    </div>
  );
}
